import { type PointerEvent as ReactPointerEvent, type RefObject, useRef, useState } from 'react';
import { appCommands, appContext } from '../app/context';
import { formatShortcut } from '../core/input/shortcuts';
import type { Rect, Vec2 } from '../core/model/types';
import {
  clampRect,
  defaultSnapTargets,
  HANDLE_CURSORS,
  HANDLES,
  type HandleId,
  hitTestHandle,
  isInsideRect,
  moveRect,
  rectFromPoints,
  resizeRect,
  snapRect,
} from '../core/selection';
import type { Viewport } from '../core/view';
import { useEditorStore } from '../state/editorStore';
import { Button } from './components/controls';
import { useGuideDrag } from './hooks/useGuideDrag';
import { clientToStage, docToStage, stageToDoc } from './pointer';

/**
 * The marquee and the guides, drawn as DOM above the canvas.
 *
 * Deliberately DOM rather than canvas: handles are hit-tested in document
 * space by the same pure functions the tests cover, and the browser gives
 * correct cursor and capture behaviour for free.
 *
 * The interaction surface only accepts pointers while the Crop tool is active,
 * so it can never steal a click from a future painting tool.
 */

type Drag =
  | { kind: 'new'; origin: Vec2 }
  | { kind: 'move'; origin: Vec2; start: Rect }
  | { kind: 'resize'; handle: HandleId; origin: Vec2; start: Rect };

const HANDLE_STYLE: Record<HandleId, { left: string; top: string }> = {
  nw: { left: '0%', top: '0%' },
  n: { left: '50%', top: '0%' },
  ne: { left: '100%', top: '0%' },
  e: { left: '100%', top: '50%' },
  se: { left: '100%', top: '100%' },
  s: { left: '50%', top: '100%' },
  sw: { left: '0%', top: '100%' },
  w: { left: '0%', top: '50%' },
};

export function SelectionLayer({ stageRef }: { stageRef: RefObject<HTMLDivElement | null> }) {
  const selection = useEditorStore((s) => s.selection);
  const guides = useEditorStore((s) => s.guides);
  const viewport = useEditorStore((s) => s.viewport);
  const interactive = useEditorStore((s) => s.activeTool === 'crop');
  const dragRef = useRef<Drag | null>(null);
  const [cursor, setCursor] = useState('crosshair');
  const startGuide = useGuideDrag(stageRef);

  const pointFrom = (event: { clientX: number; clientY: number }): Vec2 | null => {
    const stage = stageRef.current;
    if (!stage) return null;
    return stageToDoc(
      clientToStage(event.clientX, event.clientY, stage),
      useEditorStore.getState().viewport,
    );
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    // A pan gesture (space or middle button) is handled by the stage in capture
    // phase and calls preventDefault; honour that instead of starting a marquee.
    if (event.defaultPrevented || event.button !== 0) return;
    const point = pointFrom(event);
    if (!point) return;

    const store = useEditorStore.getState();
    const tolerance = 8 / store.viewport.zoom;
    const current = store.selection;

    let drag: Drag;
    if (current && hitTestHandle(current, point, tolerance)) {
      drag = {
        kind: 'resize',
        handle: hitTestHandle(current, point, tolerance) as HandleId,
        origin: point,
        start: current,
      };
    } else if (current && isInsideRect(current, point)) {
      drag = { kind: 'move', origin: point, start: current };
    } else {
      drag = { kind: 'new', origin: point };
    }

    dragRef.current = drag;
    if (drag.kind === 'new') {
      store.setSelection({ x: point.x, y: point.y, width: 0, height: 0 });
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const point = pointFrom(event);
    if (!point) return;
    const store = useEditorStore.getState();
    const bounds: Rect = { x: 0, y: 0, width: store.doc.width, height: store.doc.height };
    const drag = dragRef.current;

    if (!drag) {
      // Hover only: keep the cursor honest about what a click would do.
      const tolerance = 8 / store.viewport.zoom;
      const handle = store.selection ? hitTestHandle(store.selection, point, tolerance) : null;
      const next = handle
        ? HANDLE_CURSORS[handle]
        : store.selection && isInsideRect(store.selection, point)
          ? 'move'
          : 'crosshair';
      setCursor((previous) => (previous === next ? previous : next));
      return;
    }

    const delta = { x: point.x - drag.origin.x, y: point.y - drag.origin.y };
    const snapping = defaultSnapTargets(bounds, store.guides);
    const threshold = 6 / store.viewport.zoom;

    if (drag.kind === 'new') {
      store.setSelection(
        snapRect(clampRect(rectFromPoints(drag.origin, point), bounds), snapping, threshold).rect,
      );
    } else if (drag.kind === 'move') {
      const moved = moveRect(drag.start, delta, bounds);
      store.setSelection(snapRect(moved, snapping, threshold).rect);
    } else {
      store.setSelection(resizeRect(drag.start, drag.handle, delta, bounds));
    }
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    const selectionNow = useEditorStore.getState().selection;
    if (selectionNow && (selectionNow.width < 1 || selectionNow.height < 1)) {
      useEditorStore.getState().setSelection(null);
    }
  };

  return (
    <>
      {guides.map((guide, index) => {
        const at = docToStage(
          guide.axis === 'x' ? { x: guide.position, y: 0 } : { x: 0, y: guide.position },
          viewport,
        );
        const horizontal = guide.axis === 'y';
        return (
          // A guide is a pointer drag handle; there is no honest ARIA role for
          // "draggable ruler guide", so inventing one would be worse than
          // labelling nothing. Keyboard guide editing is a known gap.
          // biome-ignore lint/a11y/noStaticElementInteractions: pointer-only affordance, see above
          <div
            key={`${guide.axis}-${guide.position}`}
            data-testid={`guide-${guide.axis}-${index}`}
            className="pointer-events-auto absolute"
            style={
              horizontal
                ? { left: 0, right: 0, top: at.y - 3, height: 7, cursor: 'ns-resize' }
                : { top: 0, bottom: 0, left: at.x - 3, width: 7, cursor: 'ew-resize' }
            }
            onPointerDown={startGuide(guide.axis, index)}
            onDoubleClick={() => useEditorStore.getState().removeGuide(index)}
          >
            <div
              className="absolute bg-accent"
              style={
                horizontal
                  ? { left: 0, right: 0, top: 3, height: 1 }
                  : { top: 0, bottom: 0, left: 3, width: 1 }
              }
            />
          </div>
        );
      })}

      {selection ? <SelectionMarquee selection={selection} viewport={viewport} /> : null}
      {selection && interactive ? (
        <SelectionActions selection={selection} viewport={viewport} />
      ) : null}

      <div
        data-testid="selection-surface"
        className="absolute inset-0"
        style={{ pointerEvents: interactive ? 'auto' : 'none', cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
    </>
  );
}

/** Buttons anchored to the marquee, so the crop is one click, not a menu hunt. */
function SelectionActions({ selection, viewport }: { selection: Rect; viewport: Viewport }) {
  const origin = docToStage({ x: selection.x, y: selection.y + selection.height }, viewport);
  const hostSize = useEditorStore((s) => s.hostSize);

  const run = async (id: string) => {
    const result = await appCommands.execute(id, appContext);
    if (!result.ok) appContext.notify(result.reason, 'warn');
  };

  return (
    <div
      data-testid="selection-actions"
      className="absolute z-10 flex items-center gap-1.5 rounded-md border border-line bg-surface/95 px-1.5 py-1 shadow-lg"
      style={{
        left: Math.max(4, Math.min(origin.x, hostSize.width - 230)),
        top: Math.min(origin.y + 10, hostSize.height - 40),
      }}
    >
      <Button
        variant="primary"
        className="px-2 py-0.5 text-[11px]"
        onClick={() => void run('image.crop')}
        data-testid="apply-crop"
        title={`${formatShortcut('mod+shift+x')} to crop`}
      >
        Crop
      </Button>
      <Button
        className="px-2 py-0.5 text-[11px]"
        onClick={() => useEditorStore.getState().setSelection(null)}
        title="Esc to clear"
      >
        Clear
      </Button>
      <span className="pr-1 text-[10px] text-faint">Drag to redraw · handles to resize</span>
    </div>
  );
}

/** Dim everything outside the marquee and draw the handles. */
function SelectionMarquee({ selection, viewport }: { selection: Rect; viewport: Viewport }) {
  const hostSize = useEditorStore((s) => s.hostSize);
  const origin = docToStage({ x: selection.x, y: selection.y }, viewport);
  const width = selection.width * viewport.zoom;
  const height = selection.height * viewport.zoom;

  const scrim = [
    { id: 'above', left: 0, top: 0, width: hostSize.width, height: Math.max(0, origin.y) },
    {
      id: 'below',
      left: 0,
      top: origin.y + height,
      width: hostSize.width,
      height: Math.max(0, hostSize.height - (origin.y + height)),
    },
    { id: 'left', left: 0, top: origin.y, width: Math.max(0, origin.x), height },
    {
      id: 'right',
      left: origin.x + width,
      top: origin.y,
      width: Math.max(0, hostSize.width - (origin.x + width)),
      height,
    },
  ];

  return (
    <div className="pointer-events-none absolute inset-0" data-testid="selection-marquee">
      {scrim.map(({ id, ...style }) => (
        <div key={id} className="absolute bg-black/45" style={style} />
      ))}
      <div
        className="absolute"
        style={{
          left: origin.x,
          top: origin.y,
          width,
          height,
          boxShadow: '0 0 0 1px rgba(0,0,0,0.9), inset 0 0 0 1px rgba(255,255,255,0.9)',
        }}
      >
        {HANDLES.map((handle) => (
          <span
            key={handle}
            className="absolute h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rounded-[1px] border border-black bg-white"
            style={HANDLE_STYLE[handle]}
          />
        ))}
        <span className="absolute top-[3px] left-1/2 -translate-x-1/2 rounded bg-black/75 px-1.5 py-px text-[10px] tabular-nums text-white">
          {Math.round(selection.width)} × {Math.round(selection.height)}
        </span>
      </div>
    </div>
  );
}
